import React from "react";
import { RateAnalysis, QuoteRequest } from "../../types/rate-analysis";
import { formatCurrency, formatRate } from "../../utils/numberFormatting";
import { Star } from "lucide-react";

interface RefinanceEstimateResultsProps {
  analysis: RateAnalysis;
  quoteRequest: QuoteRequest;
}

const RefinanceEstimateResults: React.FC<RefinanceEstimateResultsProps> = ({
  analysis,
  quoteRequest,
}) => {
  const quotes = quoteRequest.quotes || [];
  const remainingBalance = quoteRequest.remaining_balance || 0;

  return (
    <>
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
          Your Refinance Analysis
        </h1>
        <p className="text-xl text-gray-600">
          Here are some refinance options for your loan scenario
        </p>
      </div>

      {/* Loan Scenario Summary */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          Loan Scenario
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 mb-2">
              {formatCurrency(remainingBalance)}
            </div>
            <div className="text-sm text-gray-600">Loan Amount</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 mb-2">
              {quoteRequest.credit_score}
            </div>
            <div className="text-sm text-gray-600">Credit Score</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 mb-2">
              {quoteRequest.zip_code}
            </div>
            <div className="text-sm text-gray-600">Location</div>
          </div>
        </div>
      </div>

      {/* Quote Estimates */}
      {quotes.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Refinance Options
          </h2>

          {/* Mobile View */}
          <div className="md:hidden space-y-4">
            {quotes.map((quote, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-2xl font-bold text-primary">
                    {formatRate(quote.rate)}%
                  </div>
                  {quote.isRecommended && (
                    <span className="bg-primary text-white px-2 py-1 rounded-full text-xs font-medium">
                      Recommended
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lender Fees:</span>
                    <span className="font-medium">
                      {quote.lenderFees < 0 ? (
                        <span className="text-green-600">
                          -${formatCurrency(Math.abs(quote.lenderFees))}
                        </span>
                      ) : (
                        <span>${formatCurrency(quote.lenderFees)}</span>
                      )}
                    </span>
                  </div>
                  {quote.mortgageInsurance > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mortgage Insurance:</span>
                      <span className="font-medium">
                        ${formatCurrency(quote.mortgageInsurance)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Payment:</span>
                    <span className="font-medium">
                      ${formatCurrency(quote.monthlyPayment || 0)}
                    </span>
                  </div>
                  {quote.apr && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">APR:</span>
                      <span className="font-medium">
                        {formatRate(quote.apr)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    Rate
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    Lender Fees
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    Monthly Payment
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    APR
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <span className="text-2xl font-bold text-primary">
                          {formatRate(quote.rate)}%
                        </span>
                        {quote.isRecommended && (
                          <Star size={16} className="text-primary ml-2" />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {quote.lenderFees < 0 ? (
                        <span className="text-green-600 font-medium">
                          -${formatCurrency(Math.abs(quote.lenderFees))}
                        </span>
                      ) : (
                        <span className="font-medium">
                          ${formatCurrency(quote.lenderFees)}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium">
                      ${formatCurrency(quote.monthlyPayment || 0)}
                    </td>
                    <td className="py-3 px-4">
                      {quote.apr ? (
                        <span className="font-medium">
                          {formatRate(quote.apr)}%
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {quote.isRecommended ? (
                        <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                          Recommended
                        </span>
                      ) : (
                        <span className="text-gray-500">Available</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Overall Recommendation */}
      {analysis.recommendations && analysis.recommendations.length > 0 && (
        <div className="bg-primary-light/10 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-primary mb-4">
            Overall Recommendation
          </h3>
          <p className="text-gray-700">{analysis.recommendations[0]}</p>
        </div>
      )}

      {/* Additional Information */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Next Steps</h3>
        <p className="text-blue-800 mb-4">
          These are estimated rates based on your loan scenario. To get your
          actual refinance options and lock in a rate, you'll need to speak with
          a mortgage professional.
        </p>
        <ul className="text-blue-800 space-y-2">
          <li>• Rates are subject to change based on market conditions</li>
          <li>• Final terms will depend on your complete financial profile</li>
          <li>• Closing costs may vary based on your specific situation</li>
        </ul>
      </div>
    </>
  );
};

export default RefinanceEstimateResults;
